type VideoProvider = "youtube" | "vimeo";

export type VideoEmbed = {
    provider: VideoProvider;
    videoId: string;
    embedUrl: string;
    thumbnailUrl: string | null;
};

const YOUTUBE_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

function parseYoutubeId(raw: string): string | null {
    try {
        const url = new URL(raw);
        const host = url.hostname.replace(/^www\./, "");
        if (host === "youtu.be") {
            const id = url.pathname.replace(/^\//, "");
            return YOUTUBE_ID_PATTERN.test(id) ? id : null;
        }
        if (host === "youtube.com" || host === "m.youtube.com") {
            const id = url.searchParams.get("v");
            return id && YOUTUBE_ID_PATTERN.test(id) ? id : null;
        }
        return null;
    } catch {
        return YOUTUBE_ID_PATTERN.test(raw) ? raw : null;
    }
}

function parseVimeoId(raw: string): string | null {
    try {
        const url = new URL(raw);
        const host = url.hostname.replace(/^www\./, "");
        if (host !== "vimeo.com") return null;
        const id = url.pathname.replace(/^\//, "").split("/")[0];
        return /^\d+$/.test(id) ? id : null;
    } catch {
        return null;
    }
}

// URL for embedding the provider's own player without autoplay, so its native
// poster + play button show. iOS Safari only plays a cross-origin video with
// sound when the user taps the player's own control, so the first tap must land
// on that native button. `playsinline` keeps it in place instead of fullscreen.
export function buildPlayerSrc(embed: VideoEmbed): string {
    if (embed.provider === "youtube") {
        return `https://www.youtube.com/embed/${embed.videoId}?rel=0&playsinline=1`;
    }
    return `https://player.vimeo.com/video/${embed.videoId}?playsinline=1`;
}

export function parseVideoEmbed(
    raw: string | null | undefined,
): VideoEmbed | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;

    const youtubeId = parseYoutubeId(trimmed);
    if (youtubeId) {
        return {
            provider: "youtube",
            videoId: youtubeId,
            embedUrl: `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`,
            thumbnailUrl: `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`,
        };
    }

    const vimeoId = parseVimeoId(trimmed);
    if (vimeoId) {
        return {
            provider: "vimeo",
            videoId: vimeoId,
            embedUrl: `https://player.vimeo.com/video/${vimeoId}?autoplay=1`,
            thumbnailUrl: null,
        };
    }

    return null;
}
